import { ShieldCheck, Trash } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { CapabilityEffect } from "~/api/aiAdminTypes";
import {
  useCapabilityCatalog,
  useCapabilityPolicies,
  useCreateCapabilityPolicy,
  useCurrentWorkspace,
  useDeleteCapabilityPolicy,
} from "~/api/queries";
import { ABILITY } from "~/api/types";
import { Pill, Row, Rows } from "~/features/automation/components";
import { Select } from "~/features/settings/controls";
import { skillTemplateLabel } from "~/features/settings/sections/thinkers/labels";
import { Button } from "~/foundation/components/Button";
import { Empty } from "~/foundation/components/Empty";
import { IconButton } from "~/foundation/components/IconButton";
import { Input } from "~/foundation/components/Input";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { usePolicy } from "~/policies/usePolicy";

/** A skill that can change or run things is worth showing differently from one that reads. */
function sideEffectTone(sideEffect: string): "neutral" | "warning" | "danger" {
  if (sideEffect === "system") {
    return "danger";
  }
  if (sideEffect === "write") {
    return "warning";
  }
  return "neutral";
}

/**
 * Which skills and MCP tools each kind of thinker may use.
 *
 * The default posture matters more than any rule on this page and is stated, not implied:
 * everything is permitted except the locked `system` class. So a rule here either **restricts**
 * (deny) or **extends** (allow), and an empty list is a working configuration rather than a
 * missing one — which is exactly what the empty state says.
 *
 * There is no edit: the server offers create and delete only, and pretending otherwise would
 * mean silently deleting and re-creating a rule under a different id.
 */
export function AiPermissions() {
  const { t } = useTranslation();
  const { data: policies, isLoading } = useCapabilityPolicies();
  const { data: catalog } = useCapabilityCatalog();
  const { data: workspace } = useCurrentWorkspace();
  const canAdminister = usePolicy(workspace)[ABILITY.workspaceUpdate] === true;
  const create = useCreateCapabilityPolicy();
  const remove = useDeleteCapabilityPolicy();

  const [effect, setEffect] = useState<CapabilityEffect>("deny");
  const [principalKind, setPrincipalKind] = useState("thinker_template");
  const [principalId, setPrincipalId] = useState("author");
  const [permissionKey, setPermissionKey] = useState("");

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }

  const skills = catalog?.skills ?? [];
  const templates = catalog?.thinkerTemplates ?? [];
  const chosenKey = permissionKey || skills[0]?.permissionKey || "";

  const submit = () => {
    if (!principalId.trim() || !chosenKey) {
      toast.error(t("Choose who the rule is about and what it covers."));
      return;
    }
    create.mutate(
      {
        principalKind,
        principalId: principalId.trim(),
        permissionKey: chosenKey,
        effect,
      },
      { onSuccess: () => toast.success(t("Rule added")) },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <Text variant="small">
        {/* The server's own sentence about the default, shown rather than paraphrased. */}
        {catalog?.note ??
          t(
            "Everything is permitted by default except system capabilities. Add a deny rule to restrict, or an allow rule to extend.",
          )}
      </Text>

      {canAdminister ? (
        <div className="flex flex-wrap items-end gap-2 rounded-md border border-divider p-3">
          <Select
            label={t("Effect")}
            value={effect}
            onChange={(event) => setEffect(event.target.value as CapabilityEffect)}
          >
            <option value="deny">{t("Deny")}</option>
            <option value="allow">{t("Allow")}</option>
          </Select>

          <Select
            label={t("Applies to")}
            value={principalKind}
            onChange={(event) => {
              setPrincipalKind(event.target.value);
              setPrincipalId(event.target.value === "thinker_template" ? "author" : "");
            }}
          >
            {(catalog?.principalKinds ?? ["thinker_template"]).map((kind) => (
              <option key={kind} value={kind}>
                {kind === "thinker_template"
                  ? t("A kind of thinker")
                  : kind === "thinker"
                    ? t("One thinker")
                    : kind === "user"
                      ? t("One person")
                      : t("A role")}
              </option>
            ))}
          </Select>

          {principalKind === "thinker_template" ? (
            <Select
              label={t("Which")}
              value={principalId}
              onChange={(event) => setPrincipalId(event.target.value)}
            >
              {templates.map((template) => (
                <option key={template} value={template}>
                  {skillTemplateLabel(template, t)}
                </option>
              ))}
            </Select>
          ) : (
            <Input
              label={t("Which")}
              value={principalId}
              placeholder={t("An id")}
              className="min-w-44"
              onChange={(event) => setPrincipalId(event.target.value)}
            />
          )}

          <Select
            label={t("Capability")}
            value={chosenKey}
            className="min-w-56"
            onChange={(event) => setPermissionKey(event.target.value)}
          >
            {skills.map((skill) => (
              <option key={skill.permissionKey} value={skill.permissionKey}>
                {skill.name} ({skill.permissionKey})
              </option>
            ))}
            {(catalog?.wildcards ?? []).map((wildcard) => (
              <option key={wildcard} value={wildcard}>
                {wildcard}
              </option>
            ))}
          </Select>

          <Button variant="primary" disabled={create.isPending} onClick={submit}>
            {t("Add rule")}
          </Button>
        </div>
      ) : null}

      {policies?.length ? (
        <Rows>
          {policies.map((policy) => {
            const skill = skills.find((entry) => entry.permissionKey === policy.permissionKey);
            return (
              <Row key={policy.id}>
                <Pill tone={policy.effect === "deny" ? "danger" : "success"}>
                  {policy.effect === "deny" ? t("Deny") : t("Allow")}
                </Pill>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-body text-text">
                    {skill?.name ?? policy.permissionKey}
                  </p>
                  <p className="truncate text-small text-text-tertiary">
                    {t("for {{principal}}", {
                      principal:
                        policy.principalKind === "thinker_template"
                          ? skillTemplateLabel(policy.principalId, t)
                          : policy.principalId,
                    })}
                  </p>
                </div>
                {skill ? (
                  <Pill tone={sideEffectTone(skill.sideEffect)}>{skill.sideEffect}</Pill>
                ) : null}
                <code className="hidden text-small text-text-tertiary md:inline">
                  {policy.permissionKey}
                </code>
                {canAdminister ? (
                  <IconButton
                    icon={Trash}
                    aria-label={t("Remove")}
                    size="sm"
                    disabled={remove.isPending}
                    onClick={() =>
                      remove.mutate(policy.id, {
                        onSuccess: () => toast.success(t("Rule removed")),
                      })
                    }
                  />
                ) : null}
              </Row>
            );
          })}
        </Rows>
      ) : (
        <Empty
          icon={ShieldCheck}
          title={t("No rules")}
          // An empty list is a working configuration, not a missing one.
          message={t(
            "Everything is permitted except system capabilities. That is the default, and it is in force.",
          )}
        />
      )}
    </div>
  );
}
